import React, { useEffect, useState } from 'react';
import { Typography, Box, Alert } from '@mui/material';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import BorderedButton from '../common/buttons/BorderedButton';
import { useSearchParams } from 'react-router-dom';
import GradientCircularProgress from '../common/GradientCircularProgress';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';

const app_co = process.env.REACT_APP_COMPANY;
const noop = () => {};

interface Step4Props {
  onComplete: () => void;
  fetchFormLinks: (token: string, setFormLinksNotFound: any, setLoading: any) => any;
  fetchClientBookingDetails: (token: string, setClientBookingDetailsNotFound: any, setLoading: any) => any;
  inviteDetails: any;
  inviteAcceptanceStatus: 'idle' | 'success' | 'error' | 'skipped';
  inviteAcceptanceError: string | null;
}

const SignupStep4Complete: React.FC<Step4Props> = ({
  onComplete,
  fetchFormLinks,
  fetchClientBookingDetails,
  inviteDetails,
  inviteAcceptanceStatus,
  inviteAcceptanceError,
}) => {
  const { theme } = useThemeContext();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('Your Company');
  const [formLinks, setFormLinks] = useState<{ [label: string]: string }[]>([]);

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) {
      const fetchData = async () => {
        try {
          const cb_details = await fetchClientBookingDetails(t, noop, noop);
          setCompany(cb_details.body.company);

          const formLinksResponse = await fetchFormLinks(t, noop, noop);
          if (formLinksResponse?.body?.form_links) {
            setFormLinks(formLinksResponse.body.form_links);
          } else {
            console.warn('No form links found:', formLinksResponse?.body?.message);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        <GradientCircularProgress loadingMessage="Loading ..." />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        alignItems: { xs: 'center', md: 'flex-start' },
        display: 'flex',
        textAlign: 'center',
        py: 4,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      <Box sx={{ width: { xs: '25%', md: '90%' } }}>
        {app_co === 'rh' ? (
          <img width="100%" src="/images/registration-complete.png" alt="Registration Complete" />
        ) : (
          <img width="100%" src="/images/frost/frost-thankyou.png" alt="Registration Complete" />
        )}
      </Box>
      <Box
        sx={{
          padding: '0px 30px',
          textAlign: { xs: 'center', md: 'left' },
        }}
      >
        {inviteAcceptanceStatus === 'success' && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {inviteDetails?.organization?.name
              ? `You're now part of ${inviteDetails.organization.name}${
                  inviteDetails?.invite?.invite_role ? ` as a ${inviteDetails.invite.invite_role}.` : '.'
                }`
              : 'Your invitation has been confirmed.'}
          </Alert>
        )}
        {inviteAcceptanceStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {inviteAcceptanceError || 'We were unable to complete your invitation. Please contact support.'}
          </Alert>
        )}
        {app_co === 'rh' ? (
          <></>
        ) : (
          <Typography paragraph sx={{ color: theme.custom.components.signupStep4Complete.typography.color }}>
            {company}
          </Typography>
        )}
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: theme.custom.components.signupStep4Complete.typography.color }}
        >
          {app_co === 'rh' ? 'Registration was successfully completed' : 'Thanks! Your account is now activated'}
        </Typography>
        <Typography paragraph sx={{ color: theme.custom.components.signupStep4Complete.typography.color }}>
          Your account has been created and you are now signed in.
        </Typography>
        {app_co === 'rh' ? (
          <BorderedButton
            sxProps={{
              marginLeft: { xs: '0', sm: '10px' },
            }}
            additionalProps={{
              style: { height: '41px' },
            }}
            onClick={onComplete}
            buttonText={'Go to Dashboard'}
          />
        ) : (
          <>
            {formLinks.length > 0 && (
              <Box sx={{ mt: 2 }} display="flex" flexDirection="column">
                {formLinks.map((linkObj, index) => {
                  const [label, url] = Object.entries(linkObj)[0];

                  const handleClick = () => {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  };

                  return (
                    <PrimaryBlueButton
                      key={index}
                      buttonText={label}
                      onClick={handleClick}
                      isLoading={false}
                      sxProps={{ mb: 1 }}
                    />
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SignupStep4Complete;
